import { useState, useRef } from 'react'
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Vehicle } from "@/types/interfaces/vehicle"
import { motion, AnimatePresence } from "framer-motion"
import { PlaceholderImage } from './PlaceholderImage'
import { DollarSign, Gauge, Award, Tag } from "lucide-react"
import { formatPrice, normalizeCurrency, type CurrencyMode } from "@/lib/currency"

interface VehicleCardProps extends Vehicle {
  onClick: () => void
  /** `dense` = tarjeta compacta para cuadrícula de 2 columnas. */
  dense?: boolean
  displayPrice: number
  secondaryPrice: number
  showInitialPrice: boolean
  isAuthenticated?: boolean
  /** Modo de moneda elegido por el usuario (el precio se guarda en `currency`). */
  displayCurrency?: CurrencyMode
}

export function VehicleCard({
  name,
  category,
  cc,
  images,
  brand,
  currency,
  onClick,
  dense = false,
  displayPrice,
  secondaryPrice,
  showInitialPrice,
  displayCurrency = "ORIGINAL",
}: VehicleCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [, setIsHovered] = useState(false)

  const cardRef = useRef<HTMLDivElement>(null)
  const imageUrls = images || []
  /** Moneda en la que está almacenado el precio de ESTE vehículo. */
  const source = normalizeCurrency(currency)
  const fmt = (price: number | null | undefined) =>
    formatPrice(price ?? 0, source, displayCurrency)

  // `displayPrice` ya es el precio del modo activo (inicial o total).
  const initialAmount = showInitialPrice ? displayPrice : secondaryPrice
  const totalAmount = showInitialPrice ? secondaryPrice : displayPrice

  // ─── Variante densa (cuadrícula 2 columnas) ───
  if (dense) {
    return (
      <Card
        ref={cardRef}
        className="relative cursor-pointer overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.07)] transition-all duration-300 hover:border-[#E10F18]/35 hover:shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
        onClick={onClick}
      >
        <CardContent className="p-0">
          <div className="relative aspect-[4/3] overflow-hidden">
            {imageUrls.length > 0 ? (
              <img
                src={imageUrls[0]}
                alt={name}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <PlaceholderImage />
            )}
            <div className="absolute left-2 top-2">
              <Badge variant="secondary" className="bg-white/95 text-[10px] font-semibold shadow-sm">
                {category?.name}
              </Badge>
            </div>
          </div>
          <div className="space-y-1.5 p-2.5">
            <h3 className="truncate text-sm font-semibold leading-tight text-gray-900">
              {name}
            </h3>
            <p className="truncate text-xs text-gray-500">{brand?.name}</p>
            <div className="flex items-baseline justify-between pt-1">
              <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                {showInitialPrice ? "Inicial" : "Total"}
              </span>
              <span className="text-sm font-bold text-[#E10F18]">
                {fmt(displayPrice)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // ─── Variante por defecto (tarjeta grande) ───
  return (
    <Card
      ref={cardRef}
      className="relative overflow-hidden transition-all duration-300 cursor-pointer group
        bg-white rounded-2xl border border-gray-200/80 hover:border-[#E10F18]/35
        shadow-[0_12px_30px_rgba(15,23,42,0.08)] hover:shadow-[0_18px_40px_rgba(15,23,42,0.16)]
        transform hover:-translate-y-1"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setCurrentImageIndex(0) }}
      onClick={onClick}
    >
      <CardContent className="p-0">
        {/* Image Section */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <AnimatePresence mode="wait">
            {imageUrls.length > 0 ? (
              <motion.img
                key={currentImageIndex}
                src={imageUrls[currentImageIndex]}
                alt={name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              />
            ) : (
              <PlaceholderImage />
            )}
          </AnimatePresence>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-black/45 to-transparent" />

          {/* Category Badge */}
          <div className="absolute top-3 left-3">
            <Badge variant="secondary" className="bg-white/95 backdrop-blur-sm shadow-sm text-[11px] font-semibold">
              {category.name}
            </Badge>
          </div>

          {/* Image Navigation Dots */}
          {imageUrls.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {imageUrls.map((_, index) => (
                <motion.div
                  key={index}
                  className={`rounded-full bg-white/80 cursor-pointer transition-all duration-200
                    ${currentImageIndex === index ? 'w-6 h-2' : 'w-2 h-2'}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setCurrentImageIndex(index)
                  }}
                  layout
                />
              ))}
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-3 sm:p-4 space-y-3">
          {/* Vehicle Name */}
          <h3 className="font-semibold text-base sm:text-lg text-gray-900 leading-tight overflow-hidden">
            <span className="block truncate">{name}</span>
          </h3>

          {/* Vehicle Details */}
          <div className="space-y-3">
            <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2 xs:gap-4">
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <Award className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <span className="text-sm text-gray-600 font-medium truncate">
                  {brand?.name}
                </span>
              </div>

              <div className="flex items-center space-x-2 flex-shrink-0">
                <Gauge className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600 font-medium whitespace-nowrap">
                  {cc} cc
                </span>
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="pt-2 border-t border-gray-100">
            <div className="space-y-2 rounded-lg bg-[#F8F9FD] border border-gray-200/70 px-2.5 py-2">
              {/* Initial Price */}
              <div className={`flex items-center justify-between transition-all duration-200 ${
                showInitialPrice ? "text-red-600 font-semibold" : "text-gray-500"
              }`}>
                <div className="flex items-center space-x-2">
                  <Tag className={`h-4 w-4 flex-shrink-0 ${
                    showInitialPrice ? "text-red-600" : "text-gray-400"
                  }`} />
                  <span className="text-sm font-medium">Inicial:</span>
                </div>
                <span className={`text-sm font-semibold tracking-tight ${
                  showInitialPrice ? "text-red-600" : "text-gray-500"
                }`}>
                  {fmt(initialAmount)}
                </span>
              </div>

              {/* Total Price */}
              <div className={`flex items-center justify-between transition-all duration-200 ${
                !showInitialPrice ? "text-red-600 font-semibold" : "text-gray-500"
              }`}>
                <div className="flex items-center space-x-2">
                  <DollarSign className={`h-4 w-4 flex-shrink-0 ${
                    !showInitialPrice ? "text-red-600" : "text-gray-400"
                  }`} />
                  <span className="text-sm font-medium">Total:</span>
                </div>
                <span className={`text-sm sm:text-base font-bold tracking-tight ${
                  !showInitialPrice ? "text-red-600" : "text-gray-500"
                }`}>
                  {fmt(totalAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
